import React, { useEffect, useState } from 'react';
import { Typography, Grid } from '@mui/material';

import { useHistory } from 'react-router-dom';

import { useSelector } from 'react-redux';
import Divider from '@mui/material/Divider';
import moment from 'moment';
import Snackbar from '~/util/SnackBar';

import MonthlyQuotaPanel from '~/components/MonthlyQuotaPanel';

import CustomHeader from '~/components/CustomHeader';
import api, { maisVendasApi } from '~/services/api';

import { formatPrice, abbreviateFormatDate } from '~/util/format';

import getAssessmentDatesByDate from '~/util/getAssessmentDatesByDate';
import { GERENTE_ADOROCODIGO } from '~/util/perfilId';
import { MARIAFILO, FOXTON, FARM, FARM_ETC } from '~/util/brands';
import { isWithinQuotaFreezePeriod, COMMISSION_FREEZE_PERIOD } from '~/util/constants';
import * as Style from './styles';

function CotasMensais({
    setCurrentPage,
    salesTeamData,
    setSalesTeamData,
    quotaValues,
    setQuotaValues,
}) {
    const history = useHistory();
    const [salesCard, setSalesCard] = useState(undefined);
    const [loadingSalesTeamData, setLoadingSalesTeamData] = useState(false);
    const [isAllQuotaDistributed, setIsAllQuotaDistributed] = useState(false);
    const [
        distributionModeInCurrency,
        setDistributionModeInCurrency,
    ] = useState(false);

    const userProfile = useSelector(state => state.user.profile);

    const [initialDate, setInitialDate] = useState(null);
    const [finalDate, setFinalDate] = useState(null);

    const isFarmOrFarmEtc = [FARM, FARM_ETC].includes(
        Number(userProfile?.marcaFilial)
    );

    const currentDay = moment().date();
    const isPastFarmOrFarmETCCutoffDate =
        isFarmOrFarmEtc &&
        isWithinQuotaFreezePeriod(currentDay, COMMISSION_FREEZE_PERIOD);

    useEffect(() => {
        if (isPastFarmOrFarmETCCutoffDate) {
            Snackbar.warning(
            `Atribuição bloqueada para o fechamento de comissões. O sistema reabre no dia ${COMMISSION_FREEZE_PERIOD.endDay}.`,
        { autoHideDuration: 8000 });
        }
    }, [isPastFarmOrFarmETCCutoffDate]);

    useEffect(() => {
        if (!userProfile?.marcaFilial) return;

        const isMariaFiloOrFoxton = [MARIAFILO, FOXTON].includes(
            Number(userProfile.marcaFilial)
        );

        let assessmentDate = moment().endOf('month');

        if (!isMariaFiloOrFoxton && moment().date() > 25) {
            assessmentDate = assessmentDate.add(1, 'month');
        }

        const { startDate, endDate } = getAssessmentDatesByDate(
            assessmentDate,
            userProfile.marcaFilial
        );
        setInitialDate(startDate);
        setFinalDate(endDate);
    }, [userProfile?.marcaFilial]);

     function salespersonIdFilter(salesperson) {
        const apelido = (salesperson.vendedor_apelido || '').toLowerCase();

        if (apelido.includes('atend')) {
            return true;
        }

        return salesperson.id_perfil !== '99';
    }

    useEffect(() => {
        if (distributionModeInCurrency) {
            const salespeopleQuotaSum =
                Math.round(
                    salesTeamData.reduce(
                        (total, salesperson) =>
                            total + Number(salesperson.quotaValueMonth || 0),
                        0
                    ) * 100
                ) / 100;
            const storeQuota = quotaValues?.quota;
            setIsAllQuotaDistributed(salespeopleQuotaSum === storeQuota);
        } else {
            const salespeopleQuotaPercentageSum = salesTeamData.reduce(
                (total, salesperson) =>
                    total + Number(salesperson.quotaPercentageMonth || 0),
                0
            );
            setIsAllQuotaDistributed(
                Number(salespeopleQuotaPercentageSum.toFixed(2)) === 100
            );
        }
    }, [distributionModeInCurrency, quotaValues, salesTeamData]);

    useEffect(() => {
        if (!initialDate || !finalDate) return;

        const getQuota = async () => {
            const quotaResponse = await maisVendasApi.get('/store/quota', {
                params: {
                    startDate: moment(initialDate).format('YYYY-MM-DD'),
                    endDate: moment(finalDate).format('YYYY-MM-DD'),
                    filialCode:
                        userProfile.users_branches[0].codigo_filial.length === 3
                            ? `000${userProfile.users_branches[0].codigo_filial}`
                            : userProfile.users_branches[0].codigo_filial,
                },
            });
            setSalesCard(currentSalesCardData => ({
                ...currentSalesCardData,
                secondaryText: formatPrice(`${quotaResponse.data.totalQuota}`),
            }));
            setQuotaValues(currentQuotaValue => ({
                ...currentQuotaValue,
                quota: quotaResponse.data.totalQuota,
                dailyQuotas: quotaResponse.data.dailyQuotas,
            }));
        };

        getQuota();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDate, finalDate]);

    useEffect(() => {
        const getRankingData = async () => {
            try {
                if (initialDate && finalDate && quotaValues) {
                    setLoadingSalesTeamData(true);

                    const isAdoroCodigoManager =
                        userProfile.id_perfil === GERENTE_ADOROCODIGO;

                    const apiClient = isAdoroCodigoManager
                        ? maisVendasApi
                        : api;
                    const apiRoute = isAdoroCodigoManager
                        ? '/trainersmanager/trainers'
                        : '/store-manager/sales-team';
                    const apiParams = isAdoroCodigoManager
                        ? {
                              startDate: moment(initialDate).format(
                                  'YYYY-MM-DD'
                              ),
                              endDate: moment(finalDate).format('YYYY-MM-DD'),
                          }
                        : {
                              dateInit: moment(initialDate).format(
                                  'YYYY-MM-DD'
                              ),
                              dateEnd: moment(finalDate).format('YYYY-MM-DD'),
                              filialCode:
                                  userProfile.users_branches[0].codigo_filial
                                      .length === 3
                                      ? `000${userProfile.users_branches[0].codigo_filial}`
                                      : userProfile.users_branches[0]
                                            .codigo_filial,
                              filialBrandId: userProfile.marcaFilial,
                          };

                    const rankingResponse = await apiClient.get(apiRoute, {
                        params: apiParams,
                    });

                    const newRankingData = rankingResponse.data.map(
                        salesperson => ({
                            ...salesperson,
                            shortName: `${
                                salesperson.vendedor_apelido.split(' ')[0]
                            } ${
                                salesperson.vendedor_apelido.split(' ')[
                                    salesperson.vendedor_apelido.split(' ')
                                        .length - 1
                                ]
                            }`,
                            quotaValueMonth:
                                Math.round((salesperson.quota * 100) / 100) ||
                                undefined,
                            quotaPercentageMonth:
                                ((salesperson.quota / quotaValues?.quota) *
                                    10000) /
                                    100 || undefined,
                            quotaInputLabel:
                                ((salesperson.quota / quotaValues?.quota) *
                                    10000) /
                                    100 || undefined,
                        })
                    );

                    const filteredRankingData = newRankingData.filter(
                        salespersonIdFilter
                    );

                    setSalesTeamData(filteredRankingData);
                }
            } catch (e) {
                setSalesTeamData([]);
                // eslint-disable-next-line no-console
                console.log(e);
                Snackbar.warning('Falha ao buscar a lista de vendedores');
            } finally {
                setLoadingSalesTeamData(false);
            }
        };
        getRankingData();
    }, [initialDate, finalDate, quotaValues, setSalesTeamData, userProfile]);

    const handleClickOnNextPageButton = () => {
        setCurrentPage('CotasDiarias');
    };

    return (
        <>
            <CustomHeader title="Cadastro de Cotas" />
            <Grid
                container
                justifyContent="flex-start"
                alignItems="center"
                style={{
                    padding: '16px',
                }}
            >
                <Grid item xs={12}>
                    <Style.BrandContainer container>
                        <Grid item xs={12}>
                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Grid item>
                                    <Style.PrimaryTypography>
                                        Período
                                    </Style.PrimaryTypography>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        style={{
                                            fontSize: '16px',
                                        }}
                                    >
                                        {initialDate && finalDate
                                            ? `${abbreviateFormatDate(
                                                  moment(initialDate).format(
                                                      'YYYY-MM-DD'
                                                  )
                                              )} - ${abbreviateFormatDate(
                                                  moment(finalDate).format(
                                                      'YYYY-MM-DD'
                                                  )
                                              )}, ${moment(finalDate).format(
                                                  'YYYY'
                                              )}`
                                            : ''}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider variant="fullWidth" />
                        </Grid>
                        <Grid item xs={12}>
                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Grid item>
                                    <Style.PrimaryTypography>
                                        Cota da Loja
                                    </Style.PrimaryTypography>
                                </Grid>
                                <Grid item>
                                    <Style.PrimaryTypography>
                                        {salesCard?.secondaryText}
                                    </Style.PrimaryTypography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Style.BrandContainer>
                </Grid>
                <Grid
                    item
                    xs={12}
                    style={{
                        margin: '16px 12px',
                    }}
                >
                    <Style.PrimaryTypography primary>
                        Distribuição de cotas mensais
                    </Style.PrimaryTypography>
                </Grid>
                <Grid item xs={12}>
                    <MonthlyQuotaPanel
                        setSalesTeamData={setSalesTeamData}
                        salesTeamData={salesTeamData}
                        loadingSalesTeamData={loadingSalesTeamData}
                        quotaValues={quotaValues}
                        isAllQuotaDistributed={isAllQuotaDistributed}
                        distributionModeInCurrency={distributionModeInCurrency}
                        setDistributionModeInCurrency={
                            setDistributionModeInCurrency
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <Style.PrimaryButton
                        primary
                        onClick={handleClickOnNextPageButton}
                        disabled={
                            !isAllQuotaDistributed ||
                            isPastFarmOrFarmETCCutoffDate
                        }
                    >
                        Avançar
                    </Style.PrimaryButton>
                    <Style.PrimaryButton
                        onClick={() => {
                            history.push('/vendedores');
                        }}
                    >
                        Voltar
                    </Style.PrimaryButton>
                </Grid>
            </Grid>
        </>
    );
}

export default CotasMensais;
